import {
  faBars,
  faChevronLeft,
  faChevronRight,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'components/link';
import { HeaderButton, HeaderPortal } from 'components/sections/header';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { useClickAwayListener, useWindowSize } from 'utils/hooks';
import styles from 'views/teacher/components/teacher-sidebar.module.css';
import homeViewStyles from 'views/teacher/home-view/home-view.module.css';

interface ICollapsibleSidebarProps {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  secondaryHidden?: boolean;
  secondarySelect?: boolean;
  allowToggle?: boolean;
}

const sidebarCookie = 'spxrdr_sidebar_hidden';

export const CollapsibleSidebar = ({
  primary,
  secondary,
  secondaryHidden,
  secondarySelect,
  allowToggle,
}: ICollapsibleSidebarProps) => {
  const windowSize = useWindowSize();
  const [showSidebarToggle, setShowSidebarToggle] = useState(false);
  useEffect(
    () => setShowSidebarToggle(Boolean(windowSize.width && windowSize.width <= 1000)),
    [windowSize],
  );

  const [sidebarVisible, setSidebarVisible] = useState(false);
  useClickAwayListener(setSidebarVisible, sidebarVisible, (ev: MouseEvent) => {
    const clickInSidebar = ev.target instanceof Element && ev.target.closest(`[data-prevent-hide]`);
    return !clickInSidebar;
  });

  const [cookies, setCookie] = useCookies([sidebarCookie]);
  const sidebarHidden = Boolean(cookies[sidebarCookie]);
  const toggleSidebarHidden = () => {
    if (sidebarHidden) {
      setCookie(sidebarCookie, '');
    } else {
      setCookie(sidebarCookie, 'true');
    }
  };

  const sidebarCollapsable = showSidebarToggle || sidebarHidden;
  return (
    <div
      className={classNames(styles.TeacherSidebar, {
        [styles.TeacherSidebarShow]: sidebarVisible,
        [styles.TeacherSidebarCollapsible]: sidebarCollapsable,
      })}
    >
      {sidebarCollapsable && (
        <HeaderPortal>
          <HeaderButton
            icon={faBars}
            onClick={e => {
              e.stopPropagation();
              setSidebarVisible(!sidebarVisible);
            }}
          />
        </HeaderPortal>
      )}

      <div className={styles.TeacherSidebarMain}>{primary}</div>

      <div
        className={classNames(styles.TeacherSidebarScrollable, {
          [styles.TeacherSidebarScrollableClassSelect]: secondarySelect,
          [styles.TeacherSidebarScrollableClassHidden]: secondaryHidden,
        })}
      >
        {secondary}
      </div>

      {allowToggle && !showSidebarToggle && (
        <div className={styles.TeacherSidebarAfter} data-prevent-hide={true}>
          <SidebarPrimaryButton
            onClick={toggleSidebarHidden}
            name={`${sidebarHidden ? 'Show' : 'Hide'} sidebar`}
            icon={sidebarHidden ? faChevronRight : faChevronLeft}
          />
        </div>
      )}
    </div>
  );
};

type CustomIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

interface ISidebarPrimaryLinkProps {
  to: string;
  name: string;
  icon?: IconDefinition;
  customIcon?: CustomIcon;
  active?: boolean;
  preventHide?: boolean;
  showNewChip?: boolean;
}

export const SidebarPrimaryLink = ({
  to,
  active,
  icon,
  customIcon: CustomIcon,
  name,
  preventHide,
  showNewChip = false,
}: ISidebarPrimaryLinkProps) => {
  const location = useLocation();
  const showActive = active || location.pathname === to;
  return (
    <Link
      to={to}
      className={classNames(styles.TeacherSidebarButton, styles.TeacherSidebarButtonLarge, {
        [styles.TeacherSidebarButtonActive]: showActive,
      })}
      analyticsEvent={{ category: 'navigation', action: 'teacher sidebar click', labels: { name } }}
      {...(preventHide ? { 'data-prevent-hide': 'true' } : {})}
      data-testid={`teacher-sidebar-link-${name}`}
    >
      {CustomIcon && <CustomIcon className={styles.TeacherSidebarButtonIcon} />}
      {icon && <FontAwesomeIcon icon={icon} fixedWidth={true} />}
      <div className={styles.Content}>
        {name}
        {showNewChip && (
          <div
            className={homeViewStyles.NewFeatureIcon}
            style={{
              padding: '1px 4px',
              position: 'absolute',
              right: '-15px',
              fontSize: '0.75rem',
            }}
          >
            NEW
          </div>
        )}
      </div>
    </Link>
  );
};

interface ISidebarPrimaryButtonProps {
  name: string;
  icon?: IconDefinition;
  customIcon?: CustomIcon;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const SidebarPrimaryButton = ({
  name,
  icon,
  customIcon: CustomIcon,
  onClick,
}: ISidebarPrimaryButtonProps) => {
  return (
    <div
      onClick={onClick}
      className={classNames(styles.TeacherSidebarButton, styles.TeacherSidebarButtonLarge)}
      role="button"
    >
      {CustomIcon && <CustomIcon className={styles.TeacherSidebarButtonIcon} />}
      {icon && <FontAwesomeIcon icon={icon} fixedWidth={true} />}
      <span>{name}</span>
    </div>
  );
};
